import React, { useState, useMemo } from 'react';
import { NavArrowDown } from 'iconoir-react';
import { C, St, FONT_BODY, CARD_STYLE, SCHEDA_PALETTE } from '../theme';
import { getSchedaColor, setSchedaColor } from '../schedaIcons';
import { EXERCISE_DB } from '../exerciseDB';
import { calcVolume, calcTotalReps } from '../science';
import { autoAssociaEsercizi } from '../utils';

// ─── SESSION STATS OVERVIEW ───────────────────────────────────────────────────
export function SessionStats({ exercises, allSessions, date }) {
  const [showVolDetail, setShowVolDetail] = useState(false);
  const totalVol   = Math.round(exercises.reduce((s,e)=>s+calcVolume(e.sets),0));
  const totalReps  = exercises.reduce((s,e)=>s+calcTotalReps(e.sets),0);
  const totalSets  = exercises.reduce((s,e)=>s+e.sets.filter(x=>parseFloat(x.weight)>0&&parseInt(x.reps)>0).length,0);
  const avgRPEAll  = (() => {
    const vals = exercises.flatMap(e=>e.sets.filter(s=>parseFloat(s.rpe)>0).map(s=>parseFloat(s.rpe)));
    return vals.length ? vals.reduce((a,b)=>a+b,0)/vals.length : null;
  })();

  // Volume per gruppo muscolare
  const byMuscle = {};
  exercises.forEach(ex => {
    const vol = Math.round(calcVolume(ex.sets));
    const key = ex.muscle || ex.name;
    byMuscle[key] = (byMuscle[key]||0) + vol;
  });
  const maxMuscleVol = Math.max(...Object.values(byMuscle), 1);

  // Delta vs ultima sessione con stessi esercizi
  const prevSess = allSessions?.find(s =>
    s.date < (date||"9999") &&
    exercises.some(e => s.exercises?.some(pe => pe.name.toLowerCase()===e.name.toLowerCase()))
  );
  const prevTotalVol = prevSess
    ? Math.round(prevSess.exercises.reduce((s,e)=>s+calcVolume(e.sets),0))
    : null;
  const volDelta = prevTotalVol ? ((totalVol - prevTotalVol) / prevTotalVol * 100) : null;

  return (
    <div style={{...St.card, marginBottom:14}}>
      <div style={St.chip(C.blue)}>Riepilogo</div>

      {/* Hero volume */}
      <div style={{display:"flex",alignItems:"flex-end",gap:6,marginBottom:16}}>
        <span style={St.display}>{totalVol.toLocaleString()}</span>
        <span style={{fontSize:15,color:C.subtle,fontWeight:500,paddingBottom:6}}>kg·reps</span>
        {volDelta!==null&&<span style={{...St.tag(volDelta===0?C.subtle:volDelta>0?C.green:C.red),marginBottom:6,marginLeft:4}}>
          {volDelta>0?"+":""}{volDelta.toFixed(1)}%
        </span>}
      </div>

      {/* Metric rows */}
      {[
        {label:"Serie",   val:totalSets,  sub:`${exercises.length} esercizi`, pct:null},
        {label:"Reps",    val:totalReps,  sub:avgRPEAll?`RPE ${avgRPEAll.toFixed(1)}`:null, pct:null},
      ].map(s=>(
        <div key={s.label} style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
          <div style={{width:44,fontSize:12,color:C.subtle,fontWeight:500,flexShrink:0}}>{s.label}</div>
          <div style={{flex:1,background:C.fill,borderRadius:4,height:4,overflow:"hidden"}}>
            <div style={{width:"100%",height:"100%",background:C.blue,borderRadius:4}}/>
          </div>
          <div style={{fontSize:15,fontWeight:700,color:C.text,minWidth:32,textAlign:"right",...St.mono}}>{s.val}</div>
          {s.sub&&<div style={{fontSize:11,color:C.subtle,minWidth:60}}>{s.sub}</div>}
        </div>
      ))}

      {/* Volume per esercizio — collapsible */}
      {Object.keys(byMuscle).length > 1 && (
        <>
          <div style={{...St.divider,margin:"8px 0"}}/>
          <button onClick={()=>setShowVolDetail(v=>!v)}
            style={{background:"none",border:"none",padding:"4px 0",cursor:"pointer",fontFamily:FONT_BODY,display:"flex",alignItems:"center",gap:4}}>
            <span style={{fontSize:12,color:C.subtle,fontWeight:500}}>
              {showVolDetail ? "Nascondi dettaglio" : "Mostra volume per esercizio"}
            </span>
            <NavArrowDown width={10} height={10} color={C.subtle} style={{transform:showVolDetail?"rotate(180deg)":"none",transition:"transform 0.2s",flexShrink:0}}/>
          </button>
          {showVolDetail&&(
            <div style={{marginTop:8}}>
              <div style={{fontSize:11,fontWeight:600,color:C.subtle,letterSpacing:0.5,textTransform:"uppercase",marginBottom:10}}>Volume per esercizio</div>
              {Object.entries(byMuscle).sort((a,b)=>b[1]-a[1]).map(([name,vol])=>(
                <div key={name} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                  <div style={{fontSize:13,color:C.text,fontWeight:500,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</div>
                  <div style={{width:80,background:C.fill,borderRadius:3,height:4,overflow:"hidden",flexShrink:0}}>
                    <div style={{width:`${(vol/maxMuscleVol)*100}%`,height:"100%",background:C.blue,borderRadius:3,transition:"width 0.4s ease"}}/>
                  </div>
                  <div style={{...St.mono,fontSize:12,color:C.muted,minWidth:50,textAlign:"right"}}>{vol}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── COMMENT CARD ─────────────────────────────────────────────────────────────

// ─── SCHEDA SELECT ────────────────────────────────────────────────────────────
export function SchedaSelect({schede,sessions,value,onChange,placeholder="— Nessuna scheda —",inputStyle}) {
  const names=schede.length?schede.map(s=>s.name):[...new Set(sessions.map(s=>s.workoutName).filter(Boolean))];
  const base={...St.input,appearance:"none",WebkitAppearance:"none",cursor:"pointer",
    backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat:"no-repeat",backgroundPosition:"right 12px center",paddingRight:32,fontWeight:500};
  return (
    <select value={value||""} onChange={e=>onChange(e.target.value)}
      style={inputStyle?{...base,...inputStyle}:base}>
      <option value="">{placeholder}</option>
      {names.map(n=><option key={n} value={n}>{n}</option>)}
    </select>
  );
}

// ─── WELLNESS / EMOJI RATING ──────────────────────────────────────────────────

// ─── SCHEDA DETAIL SHEET ─────────────────────────────────────────────────────
export function SchedaDetailSheet({scheda, db, onSave, onClose}) {
  const isEdit = !!scheda;
  const [name, setName] = useState(scheda?.name || "");
  const [exercises, setExercises] = useState(
    (scheda?.exercises || []).map(e => ({ name: e.name, sets: typeof e.sets === 'number' ? e.sets : 3, type: e.type || 'compound', primaryMuscle: e.primaryMuscle || '' }))
  );
  const [step, setStep] = useState(isEdit ? 'sets' : 'select');
  const [selected, setSelected] = useState(() => new Set((scheda?.exercises || []).map(e => e.name)));
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [color, setColor] = useState(() => getSchedaColor(scheda?.name || '') || null);
  const [isActive, setIsActive] = useState(scheda?.active !== false);
  const [showDeactivateAlert, setShowDeactivateAlert] = useState(false);

  const suggestedCount = useMemo(() => scheda ? autoAssociaEsercizi(scheda.name, db.sessions).length : 0, [scheda, db.sessions]);
  const loadFromHistory = () => {
    const suggested = autoAssociaEsercizi(scheda.name, db.sessions);
    if (suggested.length) {
      const exs = suggested.map(e => ({ name: e.name, sets: e.sets || 3, type: e.type || 'compound', primaryMuscle: e.primaryMuscle || '' }));
      setExercises(exs);
      setSelected(new Set(exs.map(e => e.name)));
    }
  };

  const filtered = useMemo(() => EXERCISE_DB.filter(e =>
    !search || e.name.toLowerCase().includes(search.toLowerCase()) || (e.primaryMuscle || '').toLowerCase().includes(search.toLowerCase())
  ), [search]);

  const groupedFiltered = useMemo(() => {
    const map = {};
    filtered.forEach(e => { const m = e.primaryMuscle || 'Altro'; if (!map[m]) map[m] = []; map[m].push(e); });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const toggleEx = ex => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(ex.name)) next.delete(ex.name); else next.add(ex.name);
    return next;
  });

  const continua = () => {
    const newExs = [...selected].map(n => {
      const dbEx = EXERCISE_DB.find(e => e.name === n);
      return exercises.find(e => e.name === n) || { name: n, sets: 3, type: dbEx?.type || 'compound', primaryMuscle: dbEx?.primaryMuscle || '' };
    });
    setExercises(newExs);
    setStep('sets');
    setSearch('');
  };

  const adjustSets = (i, delta) => setExercises(prev => prev.map((e, j) => j === i ? { ...e, sets: Math.max(1, Math.min(10, (e.sets || 3) + delta)) } : e));
  const removeEx = i => {
    const n = exercises[i].name;
    setExercises(prev => prev.filter((_, j) => j !== i));
    setSelected(prev => { const s = new Set(prev); s.delete(n); return s; });
  };

  const save = () => {
    if (!name.trim() || exercises.length === 0) return;
    const trimmedName = name.trim();
    // Persist color by name (stable across Supabase id changes)
    if (scheda?.name && scheda.name !== trimmedName) setSchedaColor(scheda.name, null); // clear old key on rename
    setSchedaColor(trimmedName, color);
    const updated = scheda
      ? { ...db, schede: db.schede.map(s => s.id === scheda.id ? { ...s, name: trimmedName, exercises, active: isActive } : s) }
      : { ...db, schede: [...(db.schede || []), { id: Date.now(), name: trimmedName, exercises, active: true }] };
    onSave(updated);
  };
  const del = () => onSave({ ...db, schede: (db.schede || []).filter(s => s.id !== scheda?.id) });

  const canSave = name.trim() && exercises.length > 0;
  const canContinua = name.trim() && selected.size > 0;

  const GRAY_IN = { width: '100%', border: 'none', borderRadius: 14, padding: '14px 16px', background: '#F2F2F7', outline: 'none', boxSizing: 'border-box', fontFamily: FONT_BODY, fontSize: 16, color: C.text };
  const SEP = () => <div style={{ height: 1, background: 'rgba(60,60,67,0.10)', margin: '0 16px' }} />;
  const StepperBtn = ({ onPress, symbol }) => (
    <button onClick={onPress} style={{ width: 26, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', background: 'rgba(120,120,128,0.12)', color: C.text, fontSize: 17, fontWeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: FONT_BODY }}>
      {symbol}
    </button>
  );

  const navLeft = step === 'sets' && !isEdit
    ? { label: 'Indietro', action: () => setStep('select') }
    : step === 'select' && isEdit
    ? { label: 'Indietro', action: () => setStep('sets') }
    : { label: 'Annulla', action: onClose };
  const navRight = step === 'select' ? { label: 'Continua', action: continua, ok: canContinua } : { label: 'Salva', action: save, ok: canSave };
  const navTitle = step === 'select' ? (isEdit ? 'Modifica scheda' : 'Nuova scheda') : (isEdit ? 'Modifica scheda' : 'Scegli serie');

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.32)', zIndex: 300, display: 'flex', alignItems: 'flex-end' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#ffffff', borderRadius: '28px 28px 0 0', width: '100%', maxWidth: 560, margin: '0 auto', maxHeight: '92vh', display: 'flex', flexDirection: 'column', paddingBottom: 'max(20px, env(safe-area-inset-bottom, 16px))' }}>

        {/* Drag handle */}
        <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(120,120,128,0.28)', margin: '12px auto 0' }} />

        {/* Nav row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px 14px' }}>
          <button onClick={navLeft.action} style={{ background: 'none', border: 'none', color: C.blue, fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: FONT_BODY, padding: 0 }}>{navLeft.label}</button>
          <span style={{ fontSize: 17, fontWeight: 700, color: C.text }}>{navTitle}</span>
          <button onClick={navRight.action} disabled={!navRight.ok}
            style={{ background: 'none', border: 'none', color: navRight.ok ? C.blue : C.subtle, fontSize: 15, fontWeight: 600, cursor: navRight.ok ? 'pointer' : 'default', fontFamily: FONT_BODY, padding: 0 }}>
            {navRight.label}
          </button>
        </div>

        {/* ── STEP: SELECT ── */}
        {step === 'select' && (<>
          <div style={{ padding: '0 16px 10px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input placeholder="Nome scheda (es. Petto e Tricipiti)" value={name} onChange={e => setName(e.target.value)} style={GRAY_IN} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F2F2F7', borderRadius: 999, padding: '0 14px', height: 40 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.subtle} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca esercizio o muscolo…"
                style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontFamily: FONT_BODY, fontSize: 15, color: C.text }} />
              {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: C.subtle, fontSize: 18, lineHeight: 1 }}>×</button>}
            </div>
          </div>

          {selected.size > 0 && (
            <div style={{ padding: '2px 20px 6px', fontSize: 13, color: C.blue, fontWeight: 600 }}>
              {selected.size} {selected.size === 1 ? 'esercizio selezionato' : 'esercizi selezionati'}
            </div>
          )}

          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 16px 20px' }}>
            {groupedFiltered.map(([muscle, exList]) => (
              <div key={muscle} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.subtle, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6, paddingLeft: 4 }}>{muscle}</div>
                <div style={{ ...CARD_STYLE, padding: 0 }}>
                  {exList.map((ex, i) => {
                    const isSel = selected.has(ex.name);
                    return (
                      <div key={ex.name}>
                        <div onClick={() => toggleEx(ex)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', cursor: 'pointer' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{ex.name}</div>
                            <div style={{ fontSize: 11, color: C.subtle, marginTop: 1 }}>{ex.primaryMuscle}</div>
                          </div>
                          <span style={{ ...St.tag(ex.type === 'compound' ? C.blue : C.purple), fontSize: 10 }}>{ex.type === 'compound' ? 'Multi' : 'Isol.'}</span>
                          <div style={{ width: 22, height: 22, borderRadius: 11, flexShrink: 0, background: isSel ? C.blue : 'transparent', border: isSel ? 'none' : '1.5px solid rgba(60,60,67,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }}>
                            {isSel && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                          </div>
                        </div>
                        {i < exList.length - 1 && <SEP />}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>)}

        {/* ── STEP: SETS ── */}
        {step === 'sets' && (<>
          <div style={{ padding: '0 16px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input placeholder="Nome scheda" value={name} onChange={e => setName(e.target.value)} style={GRAY_IN} />
            {/* Color picker */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#F2F2F7', borderRadius: 14 }}>
              <span style={{ fontSize: 13, color: C.subtle, fontWeight: 500, flex: 1 }}>Colore</span>
              <div style={{ display: 'flex', gap: 8 }}>
                {SCHEDA_PALETTE.map(c => {
                  const isSel = (color || SCHEDA_PALETTE[0]) === c;
                  return (
                    <button key={c} onClick={() => setColor(c)} style={{
                      width: 26, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                      background: c, padding: 0, flexShrink: 0,
                      boxShadow: isSel ? `0 0 0 2px #fff, 0 0 0 4px ${c}` : 'none',
                      transform: isSel ? 'scale(1.15)' : 'scale(1)',
                      transition: 'transform 0.15s, box-shadow 0.15s',
                    }}/>
                  );
                })}
              </div>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 16px 32px' }}>
            {isEdit && suggestedCount > 0 && (
              <button onClick={loadFromHistory} style={{ width: '100%', background: `${C.blue}10`, border: `1px solid ${C.blue}28`, borderRadius: 14, padding: '12px 16px', cursor: 'pointer', fontFamily: FONT_BODY, color: C.blue, fontSize: 14, fontWeight: 600, marginBottom: 14 }}>
                Carica esercizi dallo storico
              </button>
            )}

            {exercises.length > 0 && (<>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.subtle, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6, paddingLeft: 4 }}>
                {exercises.length} {exercises.length === 1 ? 'esercizio' : 'esercizi'}
              </div>
              <div style={{ ...CARD_STYLE, padding: 0, marginBottom: 12 }}>
                {exercises.map((ex, i) => (
                  <div key={ex.name}>
                    <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ex.name}</div>
                        {ex.primaryMuscle && <div style={{ fontSize: 11, color: C.subtle, marginTop: 1 }}>{ex.primaryMuscle}</div>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <StepperBtn onPress={() => adjustSets(i, -1)} symbol="−" />
                        <div style={{ minWidth: 28, textAlign: 'center' }}>
                          <div style={{ fontSize: 17, fontWeight: 700, color: C.text, lineHeight: 1 }}>{ex.sets || 3}</div>
                          <div style={{ fontSize: 9, color: C.subtle, marginTop: 2, letterSpacing: 0.3, textTransform: 'uppercase' }}>serie</div>
                        </div>
                        <StepperBtn onPress={() => adjustSets(i, +1)} symbol="+" />
                      </div>
                      <button onClick={() => removeEx(i)} style={{ width: 26, height: 26, border: 'none', cursor: 'pointer', background: 'none', color: C.subtle, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: FONT_BODY, padding: 0 }}>×</button>
                    </div>
                    {i < exercises.length - 1 && <SEP />}
                  </div>
                ))}
              </div>
            </>)}

            <button onClick={() => setStep('select')} style={{ width: '100%', padding: '13px', border: '1.5px dashed rgba(60,60,67,0.18)', borderRadius: 14, background: 'none', color: C.blue, fontSize: 14, cursor: 'pointer', fontFamily: FONT_BODY, fontWeight: 500, marginBottom: 24 }}>
              + Aggiungi esercizi
            </button>

            {isEdit && !confirm && (
              <>
                {/* Scheda attiva toggle — solo nel container */}
                <div style={{ background: 'rgba(120,120,128,0.07)', borderRadius: 300, padding: '4px 16px', marginTop: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                    <span style={{ fontSize: 15, color: C.text, fontFamily: FONT_BODY, fontWeight: 500 }}>Scheda attiva</span>
                    <div
                      onClick={() => {
                        if (isActive) { setShowDeactivateAlert(true); }
                        else { setIsActive(true); }
                      }}
                      style={{ position: 'relative', width: 51, height: 31, borderRadius: 16,
                        background: isActive ? C.amber : 'rgba(120,120,128,0.3)',
                        transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0 }}>
                      <div style={{ position: 'absolute', top: 2, left: isActive ? 20 : 2, width: 27, height: 27,
                        borderRadius: 14, background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.18)', transition: 'left 0.2s' }}/>
                    </div>
                  </div>
                </div>
                {/* Elimina — bottone standalone */}
                <button onClick={() => setConfirm(true)} style={{ ...St.btn(C.red, true), width: '100%', marginTop: 8 }}>
                  Elimina scheda
                </button>
              </>
            )}

            {/* Alert conferma disattivazione */}
            {showDeactivateAlert && (
              <div style={{ ...CARD_STYLE, padding: 16, marginTop: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 }}>Disattivare "{scheda?.name}"?</div>
                <div style={{ fontSize: 13, color: C.subtle, marginBottom: 16 }}>La scheda non comparirà nella home e nel picker sessione.</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setShowDeactivateAlert(false)} style={{ flex: 1, background: 'rgba(120,120,128,0.12)', border: 'none', borderRadius: 12, padding: '12px', color: C.text, fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: FONT_BODY }}>Annulla</button>
                  <button onClick={() => { setIsActive(false); setShowDeactivateAlert(false); }} style={{ flex: 1, background: C.amber, border: 'none', borderRadius: 12, padding: '12px', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: FONT_BODY }}>Disattiva</button>
                </div>
              </div>
            )}
            {confirm && (
              <div style={{ ...CARD_STYLE, padding: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 }}>Eliminare "{scheda?.name}"?</div>
                <div style={{ fontSize: 13, color: C.subtle, marginBottom: 16 }}>Questa azione non può essere annullata.</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setConfirm(false)} style={{ flex: 1, background: 'rgba(120,120,128,0.12)', border: 'none', borderRadius: 12, padding: '12px', color: C.text, fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: FONT_BODY }}>Annulla</button>
                  <button onClick={del} style={{ flex: 1, background: C.red, border: 'none', borderRadius: 12, padding: '12px', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: FONT_BODY }}>Elimina</button>
                </div>
              </div>
            )}
          </div>
        </>)}
      </div>
    </div>
  );
}
